import React from 'react';
import GroupTimelineColorControls from './GroupTimelineColorControls';
import GroupTimelineConflictBadge from './GroupTimelineConflictBadge';
import GroupTimelineFilterControls from './GroupTimelineFilterControls';
import GroupTimelineSavedViewsControl from './GroupTimelineSavedViewsControl';
import type { GroupTimelineSavedView } from '../../utils/groupTimelineSavedViews';
import type { GanttSyncedOutline } from '../../utils/ganttTimelineViewSync';
import GroupTimelineFocusBanner from './GroupTimelineFocusBanner';
import GroupTimelineViewControls from './GroupTimelineViewControls';
import {
  GROUP_TIMELINE_FAMILIES,
  GROUP_TIMELINE_FAMILY_LABELS,
  isGroupTimelineSupplierFocusFamily,
  type GroupTimelineColorMode,
  type GroupTimelineFamily,
  type GroupTimelineLayoutMode,
  type GroupTimelineLegendItem,
  type GroupTimelineSortMode,
} from '../../utils/groupTimeline';
import type { GroupTimelineDetailFilter } from '../../utils/groupTimelineFilter';
import {
  hasConflictIssues,
  issuesForFamilyChip,
  type GroupTimelineConflictBoard,
  type GroupTimelineConflictIssues,
} from '../../utils/groupTimelineConflicts';
import type { GroupTimelineFocusOption } from './GroupTimelineFocusBanner';

export interface AllProjectsPaneToolbarProps {
  family: GroupTimelineFamily;
  onFamily: (family: GroupTimelineFamily) => void;
  showHotkeys?: boolean;
  hotkeyLabel?: (family: GroupTimelineFamily) => string | null;
  layout: GroupTimelineLayoutMode;
  onLayout: (layout: GroupTimelineLayoutMode) => void;
  sortMode: GroupTimelineSortMode;
  onSort: (sort: GroupTimelineSortMode) => void;
  onCollapseAll: () => void;
  onExpandAll: () => void;
  pinnedCount: number;
  pinLimit: number;
  onClearPins: () => void;
  colorMode: GroupTimelineColorMode;
  onColorMode: (mode: GroupTimelineColorMode) => void;
  legend: GroupTimelineLegendItem[];
  filter: GroupTimelineDetailFilter;
  onFilter: (filter: GroupTimelineDetailFilter) => void;
  filterOptions: {
    projects: React.ComponentProps<typeof GroupTimelineFilterControls>['projects'];
    sections: React.ComponentProps<typeof GroupTimelineFilterControls>['sections'];
    suppliers: React.ComponentProps<typeof GroupTimelineFilterControls>['suppliers'];
  };
  filterClearShortcut?: string;
  focusTasks: GroupTimelineFocusOption[];
  onRemoveFocus: (taskId: string) => void;
  conflictBoard?: GroupTimelineConflictBoard;
  onShowConflicts?: (issues: GroupTimelineConflictIssues) => void;
  savedViews?: GroupTimelineSavedView[];
  activeSavedViewId?: string | null;
  currentUserId?: string;
  ownerDisplayName?: string;
  onApplySavedView?: (view: GroupTimelineSavedView) => void;
  onCaptureSavedViewOutline?: () => GanttSyncedOutline | null;
  onSavedViewCreated?: (viewId: string) => void;
}

const AllProjectsPaneToolbar: React.FC<AllProjectsPaneToolbarProps> = ({
  family,
  onFamily,
  showHotkeys = false,
  hotkeyLabel,
  layout,
  onLayout,
  sortMode,
  onSort,
  onCollapseAll,
  onExpandAll,
  pinnedCount,
  pinLimit,
  onClearPins,
  colorMode,
  onColorMode,
  legend,
  filter,
  onFilter,
  filterOptions,
  filterClearShortcut,
  focusTasks,
  onRemoveFocus,
  conflictBoard,
  onShowConflicts,
  savedViews = [],
  activeSavedViewId = null,
  currentUserId,
  onApplySavedView,
  onCaptureSavedViewOutline,
  onSavedViewCreated,
}) => {
  return (
    <div className="pm-gantt-split-toolbar">
      <div className="pm-hub-views pm-hub-group-chips" role="tablist" aria-label="Timeline">
        {GROUP_TIMELINE_FAMILIES.map((option) => {
          const shortcut = showHotkeys ? hotkeyLabel?.(option) ?? null : null;
          const familyIssues = option === 'all' || !conflictBoard
            ? null
            : issuesForFamilyChip(conflictBoard, option);
          const showConflict = !!familyIssues && hasConflictIssues(familyIssues);
          return (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={family === option}
              className={`pm-view-btn${family === option ? ' active' : ''}${showConflict ? ' pm-view-btn--has-conflict' : ''}`}
              aria-keyshortcuts={shortcut ?? undefined}
              onClick={() => onFamily(option)}
            >
              {GROUP_TIMELINE_FAMILY_LABELS[option]}
              {familyIssues && onShowConflicts ? (
                <GroupTimelineConflictBadge
                  issues={familyIssues}
                  label={GROUP_TIMELINE_FAMILY_LABELS[option]}
                  onShowConflicts={onShowConflicts}
                />
              ) : null}
              {shortcut ? (
                <span className="pm-view-btn-shortcut-tip" role="tooltip">
                  Shortcut: <kbd>{shortcut}</kbd>
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      <GroupTimelineViewControls
        layout={layout}
        onLayoutChange={onLayout}
        sortMode={sortMode}
        onSortChange={onSort}
        onCollapseAll={onCollapseAll}
        onExpandAll={onExpandAll}
        pinnedCount={pinnedCount}
        pinLimit={pinLimit}
        onClearPins={onClearPins}
      />
      <GroupTimelineColorControls mode={colorMode} onChange={onColorMode} legend={legend} />
      {family === 'all' ? (
        <>
          <GroupTimelineFilterControls
            filter={filter}
            onChange={onFilter}
            projects={filterOptions.projects}
            sections={filterOptions.sections}
            suppliers={filterOptions.suppliers}
            clearShortcut={filterClearShortcut}
          />
          {onApplySavedView && onCaptureSavedViewOutline ? (
            <GroupTimelineSavedViewsControl
              views={savedViews}
              activeViewId={activeSavedViewId}
              currentUserId={currentUserId}
              onApply={onApplySavedView}
              onCaptureOutline={onCaptureSavedViewOutline}
              onSaved={onSavedViewCreated}
            />
          ) : null}
        </>
      ) : null}
      {isGroupTimelineSupplierFocusFamily(family) && focusTasks.length > 0 ? (
        <GroupTimelineFocusBanner extraTasks={focusTasks} onRemove={onRemoveFocus} />
      ) : null}
    </div>
  );
};

export default AllProjectsPaneToolbar;
